import React, { useState } from 'react';
import { Modal } from '../../components/Modal';
import { ModalFooter } from '../../components/ModalFooter';
import { SpinnerInput } from '../../components/SpinnerInput';
import { useRequestReviewer } from '../../hooks/useInvitations';

interface Props {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export function RequestReviewerModal({ projectId, projectName, onClose }: Props) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [numReviewers, setNumReviewers] = useState(1);
  const [dirty, setDirty] = useState(false);
  const request = useRequestReviewer(projectId);

  const startError = dirty && !startDate ? 'Start date is required' : undefined;
  const endError = dirty && !endDate ? 'End date is required' : undefined;
  const hasErrors = !!(startError || endError);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setDirty(true);
    if (hasErrors) return;
    request.mutate(
      { projectId, startDate, endDate, numReviewers },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal
      title="Request On-Demand Reviewer"
      icon="fas fa-user"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Request"
          nextDisabled={!startDate || !endDate}
          saving={request.isPending}
          savingLabel="Requesting..."
        />
      }
    >
      <div className="alert alert-info">
        By submitting this form, you are requesting we contact you in regards to adding contract
        reviewers to your project.<br /><br />
        There is no obligation in this request and no charges will be incurred.
      </div>

      <div className="form-group">
        <h4>Project: <small><i className="fas fa-gavel" aria-hidden="true" /> {projectName}</small></h4>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className={`form-group${startError ? ' has-error' : ''}`}>
            <label>Start Date</label>
            {startError && <span className="help-block">{startError}</span>}
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setDirty(true); }}
              disabled={request.isPending}
              required
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className={`form-group${endError ? ' has-error' : ''}`}>
            <label>End Date</label>
            {endError && <span className="help-block">{endError}</span>}
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setDirty(true); }}
              disabled={request.isPending}
              required
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Estimated Number of Reviewers</label>
            <SpinnerInput
              value={numReviewers}
              onChange={setNumReviewers}
              min={1}
              placeholder="Estimated Number of Reviewers"
              disabled={request.isPending}
              required
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
